import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRightLeft, Plus, Trash2, Loader2, Search, Building2, Package, Check,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface Deposito {
  id: number; codigo: string; nome: string; tipo: string;
  filial_id: number | null; ativo: boolean;
}
interface Filial { id: number; codigo: string; nome_fantasia: string }
interface Produto { id: number; codigo: string; descricao: string; unidade: string; estoque_atual: number }
interface ItemForm { produto_id: number; codigo: string; descricao: string; unidade: string; quantidade: string }

export default function TransferenciasPage() {
  const qc = useQueryClient()
  const [origem, setOrigem] = useState<string>('')
  const [destino, setDestino] = useState<string>('')
  const [busca, setBusca] = useState('')
  const [itens, setItens] = useState<ItemForm[]>([])
  const [obs, setObs] = useState('')
  const [geraFinanceiro, setGeraFinanceiro] = useState(false)
  const [geraNota, setGeraNota] = useState(false)

  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-transf'],
    queryFn: async () => (await api.get('/estoque/depositos', { params: { todas_filiais: true } })).data,
  })
  const { data: filiais = [] } = useQuery<Filial[]>({
    queryKey: ['filiais-transf'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const filiaisMap = useMemo(() => {
    const m: Record<number, Filial> = {}; filiais.forEach(f => m[f.id] = f); return m
  }, [filiais])

  const depO = depositos.find(d => d.id === Number(origem))
  const depD = depositos.find(d => d.id === Number(destino))
  const crossFilial = depO && depD && depO.filial_id !== depD.filial_id

  const { data: produtos = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-busca-transf', busca],
    queryFn: async () => busca.length > 0
      ? (await api.get('/cadastros/produtos', { params: { q: busca, limit: 20 } })).data
      : [],
    enabled: busca.length > 0,
  })

  const { data: historico = [] } = useQuery<any[]>({
    queryKey: ['transferencias-historico'],
    queryFn: async () => (await api.get('/estoque/transferencias')).data,
  })

  const adicionar = (p: Produto) => {
    if (itens.find(i => i.produto_id === p.id)) {
      toast.error('Produto já adicionado'); return
    }
    setItens(prev => [...prev, {
      produto_id: p.id, codigo: p.codigo, descricao: p.descricao,
      unidade: p.unidade, quantidade: '1',
    }])
    setBusca('')
  }
  const remover = (idx: number) => setItens(prev => prev.filter((_, i) => i !== idx))
  const setQtd = (idx: number, v: string) => setItens(prev => prev.map((i, k) => k === idx ? { ...i, quantidade: v } : i))

  const transferir = useMutation({
    mutationFn: async () => (await api.post('/estoque/transferencias', {
      deposito_origem_id: Number(origem),
      deposito_destino_id: Number(destino),
      observacao: obs || undefined,
      gera_financeiro: crossFilial ? geraFinanceiro : false,
      gera_nota: crossFilial ? geraNota : false,
      itens: itens.map(i => ({ produto_id: i.produto_id, quantidade: parseFloat(i.quantidade) || 0 })),
    })).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['transferencias-historico'] })
      qc.invalidateQueries({ queryKey: ['produtos'] })
      let msg = 'Transferência registrada'
      if (r?.cross_filial) {
        const extras: string[] = []
        if (r.financeiro_gerado) extras.push('financeiro gerado (a pagar + a receber)')
        if (r.nota_status === 'pendente_config_filial') extras.push(`NF-e CFOP ${r.cfop} pendente — configure CNPJ/certificado da filial origem`)
        if (extras.length) msg += ' — ' + extras.join('; ')
      }
      setItens([]); setObs(''); setOrigem(''); setDestino(''); setGeraFinanceiro(false); setGeraNota(false)
      toast.success(msg, { duration: 7000 })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao transferir'),
  })

  const podeTransferir = origem && destino && origem !== destino && itens.length > 0
    && itens.every(i => parseFloat(i.quantidade) > 0)

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-ink-900 flex items-center gap-2">
          <ArrowRightLeft size={20} className="text-brand-600" /> Transferência de Estoque
        </h2>
        <p className="text-sm text-ink-500 mt-0.5">
          Move produtos entre depósitos. Pode ser entre filiais (CNPJs diferentes — verifique se sua operação fiscal permite ou requer NF de transferência).
        </p>
      </div>

      <div className="card card-body space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Depósito de origem</label>
            <select className="input" value={origem} onChange={e => setOrigem(e.target.value)}>
              <option value="">Selecione...</option>
              {depositos.filter(d => d.ativo && d.id !== Number(destino)).map(d => (
                <option key={d.id} value={d.id}>
                  {d.codigo} — {d.nome}{d.filial_id ? ` · ${filiaisMap[d.filial_id]?.nome_fantasia || `Filial ${d.filial_id}`}` : ' · empresa'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Depósito de destino</label>
            <select className="input" value={destino} onChange={e => setDestino(e.target.value)}>
              <option value="">Selecione...</option>
              {depositos.filter(d => d.ativo && d.id !== Number(origem)).map(d => (
                <option key={d.id} value={d.id}>
                  {d.codigo} — {d.nome}{d.filial_id ? ` · ${filiaisMap[d.filial_id]?.nome_fantasia || `Filial ${d.filial_id}`}` : ' · empresa'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {crossFilial && (
          <div className="rounded-lg p-3 bg-amber-50 border border-amber-200 space-y-3">
            <div className="flex items-start gap-2 text-sm text-amber-900">
              <Building2 size={16} className="mt-0.5 shrink-0" />
              <div>
                <strong>Transferência entre filiais</strong> (CNPJs diferentes). CFOP automática:
                <strong> 5152</strong> mesma UF ou <strong>6152</strong> interestadual.
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input type="checkbox" checked={geraFinanceiro} onChange={e => setGeraFinanceiro(e.target.checked)} />
              <span><strong>Gerar financeiro</strong> — conta a <em>pagar</em> (origem) + a <em>receber</em> (destino), pelo custo</span>
            </label>
            <div>
              <div className="text-xs font-medium text-ink-600 mb-1">Nota fiscal</div>
              <div className="flex gap-1 bg-white rounded-md p-1 border border-amber-200 w-fit">
                <button type="button" onClick={() => setGeraNota(false)}
                  className={`px-3 py-1.5 text-xs rounded transition ${!geraNota ? 'bg-ink-700 text-white font-medium' : 'text-ink-600 hover:bg-ink-50'}`}>
                  Simples (sem nota)
                </button>
                <button type="button" onClick={() => setGeraNota(true)}
                  className={`px-3 py-1.5 text-xs rounded transition ${geraNota ? 'bg-indigo-600 text-white font-medium' : 'text-ink-600 hover:bg-ink-50'}`}>
                  Emitir NF-e de transferência
                </button>
              </div>
              {geraNota && (
                <div className="text-[11px] text-amber-700 mt-1.5">
                  ⚠ A emissão real exige a filial de <strong>origem</strong> com CNPJ + certificado A1 + IBGE próprios cadastrados.
                  Sem isso, a transferência é registrada e a nota fica <strong>pendente</strong>.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Busca de produto */}
        {origem && destino && (
          <div>
            <label className="label">Adicionar produto</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input className="input pl-9" placeholder="Buscar por código, descrição ou EAN..."
                value={busca} onChange={e => setBusca(e.target.value)}
                onKeyDown={enterAbrePrimeiro(produtos, p => adicionar(p))} />
            </div>
            {busca.length > 0 && produtos.length > 0 && (
              <div className="mt-1 bg-white border border-ink-200 rounded-lg max-h-60 overflow-auto">
                {produtos.map(p => (
                  <button key={p.id} onClick={() => adicionar(p)}
                    className="w-full text-left px-3 py-2 hover:bg-brand-50 border-b border-ink-100 last:border-0 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{p.descricao}</div>
                      <div className="text-xs text-ink-500">{p.codigo} · Estoque: {Number(p.estoque_atual).toFixed(2)} {p.unidade}</div>
                    </div>
                    <Plus size={14} className="text-brand-600" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lista de itens */}
        {itens.length > 0 && (
          <div className="border border-ink-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-ink-700">Produto</th>
                  <th className="px-3 py-2 text-right font-medium text-ink-700">Quantidade</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {itens.map((it, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.descricao}</div>
                      <div className="text-xs text-ink-500">{it.codigo}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" min="0.001" step="0.001"
                        className="input !py-1 text-right w-28 inline-block" value={it.quantidade}
                        onChange={e => setQtd(i, e.target.value)} /> <span className="text-xs text-ink-500 ml-1">{it.unidade}</span>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => remover(i)} className="p-1 rounded hover:bg-rose-50 text-rose-600">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div>
          <label className="label">Observação</label>
          <textarea className="input" rows={2} value={obs} onChange={e => setObs(e.target.value)}
            placeholder="Motivo da transferência (opcional)" />
        </div>

        <div className="flex justify-end">
          <button className="btn-primary" disabled={!podeTransferir || transferir.isPending}
            onClick={() => transferir.mutate()}>
            {transferir.isPending
              ? <><Loader2 size={14} className="animate-spin" /> Transferindo...</>
              : <><Check size={14} /> Confirmar transferência</>}
          </button>
        </div>
      </div>

      {/* Histórico */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-200 font-semibold text-sm flex items-center gap-2">
          <Package size={14} /> Últimas transferências
        </div>
        {historico.length === 0 ? (
          <div className="text-center py-8 text-ink-400 text-sm">Nenhuma transferência registrada.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b text-xs text-ink-700">
              <tr>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Origem → Destino</th>
                <th className="px-3 py-2 text-right">Itens</th>
                <th className="px-3 py-2 text-left">Obs.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {historico.map(h => {
                const dO = depositos.find(d => d.id === h.deposito_origem_id)
                const dD = depositos.find(d => d.id === h.deposito_destino_id)
                return (
                  <tr key={h.transferencia_id} className="hover:bg-ink-50">
                    <td className="px-3 py-2 text-xs text-ink-600">
                      {h.criado_em ? new Date(h.criado_em).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="font-medium">{dO?.codigo || '?'}</span>
                      <span className="text-ink-400 mx-2">→</span>
                      <span className="font-medium">{dD?.codigo || '?'}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-xs">{h.itens?.length || 0} mov.</td>
                    <td className="px-3 py-2 text-xs text-ink-600 truncate max-w-md">{h.observacao || ''}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
